'use client';

import { useState, useCallback, useMemo } from 'react';
import type { CaptureModeButton } from '@/hooks/useCamera';
import { haptics } from '@/lib/haptics';
import { getChildEntryFormRegistry } from '@/config/child-entry-form/configLoader';
import type { ChildEntryType } from '../../types';
import type { CaptureCapabilities } from './captureSchema';

export interface ConfigurableEntryType {
  value: ChildEntryType;
  label: string;
  icon: string;
}

interface UseCaptureModeButtonsParams {
  effectiveCapabilities: CaptureCapabilities;
  isProcurementSimplified: boolean;
  procurementHiddenTypes: ChildEntryType[];
  setSelectedEntryType: (type: ChildEntryType) => void;
}

/**
 * Manages the 2×2 capture button grid rendered inside the camera modal.
 *
 * - SmartAutomation (high-trust): buttons 1-3 are user-configurable expense
 *   categories persisted to localStorage; button 4 is always "Capture"
 *   (general expense). Tapping an unconfigured slot opens a config prompt.
 * - ExpensesManagementModal (zero-trust): buttons 1-3 are camera controls
 *   (flash, AF/MF toggle, auto capture); button 4 is "Capture".
 */
export function useCaptureModeButtons({
  effectiveCapabilities,
  isProcurementSimplified,
  procurementHiddenTypes,
  setSelectedEntryType,
}: UseCaptureModeButtonsParams) {
  // ── Config slot prompt state ──────────────────────────────────────────
  // When an unconfigured button (1, 2, 3) is tapped in the camera grid,
  // a prompt appears letting the user assign an expense type to that slot.
  const [configSlotIndex, setConfigSlotIndex] = useState<number | null>(null);

  const CONFIGURABLE_ENTRY_TYPES = useMemo<ConfigurableEntryType[]>(() => {
    const registry = getChildEntryFormRegistry();
    if (registry.isInitialized()) {
      const yamlTypes = registry.getConfigurableEntryTypes();
      if (yamlTypes.length > 0) {
        return yamlTypes.map(t => ({ value: t.value as ChildEntryType, label: t.label, icon: t.icon }));
      }
    }
    // Hardcoded fallback
    return [
      { value: 'fuel', label: 'Fuel', icon: '⛽' },
      { value: 'toll', label: 'Toll', icon: '🛣️' },
      { value: 'maintenance', label: 'Maintenance', icon: '🔧' },
      { value: 'supplier_invoice', label: 'Supplier Invoice', icon: '📄' },
      { value: 'delivery_note', label: 'Delivery Note', icon: '📦' },
      { value: 'casual_wage', label: 'Casual Wage', icon: '💵' },
      { value: 'site_visit', label: 'Site Visit', icon: '📍' },
      { value: 'assessment', label: 'Assessment', icon: '📋' },
      { value: 'supervision', label: 'Supervision', icon: '👁️' },
      { value: 'training', label: 'Training', icon: '📚' },
      { value: 'route_stop', label: 'Route Stop', icon: '🛑' },
      { value: 'grn', label: 'Goods Received Note', icon: '✅' },
      { value: 'purchase_return', label: 'Purchase Return', icon: '↩️' },
    ];
  }, []);

  const handleConfigSelect = useCallback((entryType: ChildEntryType) => {
    if (configSlotIndex === null) return;
    const selected = CONFIGURABLE_ENTRY_TYPES.find(t => t.value === entryType);
    if (!selected) return;

    // Read current configured categories from localStorage
    let current: Array<{ label: string; icon: string; entryType: ChildEntryType }> = [];
    try {
      const stored = localStorage.getItem('smart_automation_capture_categories');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length === 3) current = parsed;
      }
    } catch { /* ignore */ }

    // Update the specific slot
    current[configSlotIndex] = { label: selected.label, icon: selected.icon, entryType: selected.value };
    try {
      localStorage.setItem('smart_automation_capture_categories', JSON.stringify(current));
    } catch { /* ignore */ }

    setSelectedEntryType(entryType);
    setConfigSlotIndex(null);
    void haptics.light();
  }, [configSlotIndex, CONFIGURABLE_ENTRY_TYPES, setSelectedEntryType]);

  const captureModeButtons = useMemo<CaptureModeButton[]>(() => {
    if (effectiveCapabilities.canSwitchEntryTypeInCamera) {
      // ── SmartAutomation context: configurable expense buttons ──
      let configured: Array<{ label: string; icon: string; entryType: ChildEntryType } | null> = [null, null, null];
      try {
        const stored = localStorage.getItem('smart_automation_capture_categories');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed) && parsed.length === 3) {
            configured = parsed.map((c: { label: string; icon: string; entryType: ChildEntryType }) => c);
          }
        }
      } catch { /* ignore */ }

      // Filter out configured buttons referencing procurement types hidden by simplified mode
      const isHiddenType = (t: ChildEntryType) => isProcurementSimplified && procurementHiddenTypes.includes(t);

      const buttons: CaptureModeButton[] = configured.map((c, i) => {
        if (c && !isHiddenType(c.entryType)) {
          return {
            key: `prog_${i}`,
            label: c.label,
            icon: c.icon,
            isCapture: false,
            onClick: () => setSelectedEntryType(c.entryType as ChildEntryType),
          };
        }
        // Unconfigured slot — tapping prompts for config selection
        return {
          key: `prog_${i}`,
          label: String(i + 1),
          icon: '⚙️',
          isCapture: false,
          skipCapture: true,
          onClick: () => {
            setConfigSlotIndex(i);
            void haptics.medium();
          },
        };
      });

      // Button 4: always general expense, labeled "Capture"
      buttons.push({
        key: 'general_expense',
        label: 'Capture',
        icon: '🧾',
        isCapture: true,
        onClick: () => setSelectedEntryType('purchase'),
      });

      return buttons;
    } else {
      // ── ExpensesManagementModal context: camera controls ──
      return [
        { key: 'flash', label: 'Flash', icon: '🔦', cameraAction: 'flash', onClick: () => {} },
        { key: 'focus_mode', label: 'AF', icon: '🎯', cameraAction: 'focusMode', onClick: () => {} },
        { key: 'auto_capture', label: 'Auto', icon: '📷', cameraAction: 'autoCapture', onClick: () => {} },
        { key: 'capture', label: 'Capture', icon: '📸', isCapture: true, onClick: () => {} },
      ];
    }
  }, [effectiveCapabilities, isProcurementSimplified, procurementHiddenTypes, setSelectedEntryType]);

  return {
    configSlotIndex,
    setConfigSlotIndex,
    CONFIGURABLE_ENTRY_TYPES,
    handleConfigSelect,
    captureModeButtons,
  };
}

export type UseCaptureModeButtonsReturn = ReturnType<typeof useCaptureModeButtons>;
